import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";

export default function ResetPasswordPage() {
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token") || "";
    setToken(t);
    // Remove the token from the URL/history to avoid leakage.
    window.history.replaceState({}, document.title, "/reset-password");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/account/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset! Please log in.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen bg-background flex items-center justify-center px-6 py-24">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Lock className="w-12 h-12 text-primary mx-auto" />
          <h1 className="font-anton text-4xl uppercase tracking-tight mt-5 text-white">New password</h1>
          <p className="text-white/60 mt-3 text-sm">Choose a new password for your account.</p>
        </div>

        {done ? (
          <div className="border border-primary/30 bg-primary/5 p-6 text-center text-white text-sm">
            Password updated — redirecting you to login…
          </div>
        ) : !token ? (
          <div data-testid="reset-no-token" className="border border-white/15 p-6 text-center">
            <p className="text-white/70 text-sm">This reset link is missing or invalid.</p>
            <Link to="/forgot-password" className="inline-block mt-4 text-primary font-bold uppercase tracking-widest text-sm hover:text-white">Request a new link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <input
              data-testid="reset-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (min 6 chars)"
              className="w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3.5 text-white placeholder:text-white/30"
            />
            <input
              data-testid="reset-confirm-input"
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              className="w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3.5 text-white placeholder:text-white/30"
            />
            {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              data-testid="reset-submit-button"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-primary text-black hover:bg-white disabled:opacity-40 px-8 py-4 font-bold uppercase tracking-[0.2em] text-sm transition-colors active:scale-95"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Reset password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
